import React, { useState, useEffect } from 'react';
import { jobApplications, libraries } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import ConfirmDialog from '../UI/ConfirmDialog';
import { TextField, Checkbox, Select } from '@anyemp/ui-kit';

const ContactsTab = ({ applicationId }) => {
  const [contacts, setContacts] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    tool_id: '',
    value: '',
    is_primary: false
  });

  useEffect(() => {
    fetchData();
  }, [applicationId]);

  const fetchData = async () => {
    await Promise.all([
      fetchContacts(),
      fetchTools()
    ]);
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await jobApplications.getJobApplicationContacts(applicationId);
      
      if (response.success) {
        setContacts(response.data);
      } else {
        setError('Failed to fetch contacts');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Error loading contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTools = async () => {
    try {
      const response = await libraries.getContactTools();

      if (response.success) {
        setTools(response.data);
      } else {
        console.error('Failed to fetch tools');
      }
    } catch (err) {
      console.error('Error fetching tools:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingContact) {
        // Update existing contact
        await jobApplications.updateJobApplicationContact(
          applicationId, 
          editingContact.id, 
          formData
        );
      } else {
        // Create new contact
        await jobApplications.addJobApplicationContact(applicationId, formData);
      }
      
      setShowForm(false);
      setEditingContact(null);
      setFormData({ tool_id: '', value: '', is_primary: false });
      fetchContacts();
    } catch (err) {
      console.error('Error saving contact:', err);
      alert('Error saving contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      tool_id: contact.tool_id || '',
      value: contact.value,
      is_primary: contact.is_primary
    });
    setShowForm(true);
  };

  const handleDelete = (contact) => {
    setDeleteConfirm(contact);
  };

  const confirmDelete = async () => {
    try {
      await jobApplications.deleteJobApplicationContact(applicationId, deleteConfirm.id);
      fetchContacts();
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Error deleting contact');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingContact(null);
    setFormData({ tool_id: '', value: '', is_primary: false });
  };

  const isImageSource = (value) => {
    if (!value || typeof value !== 'string') return false;
    const lower = value.toLowerCase();
    return lower.startsWith('http') || lower.startsWith('data:') || lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.svg') || lower.endsWith('.webp');
  };

  const renderToolIcon = (tool) => {
    const iconValue = tool?.icon;
    if (isImageSource(iconValue)) {
      return (
        <img
          src={iconValue}
          alt={tool?.name || 'tool icon'}
          className="w-7 h-7 rounded-md object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }}
        />
      );
    }
    // Fallback: first letter avatar
    return (
      <div className="w-7 h-7 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-sm flex items-center justify-center text-light-text-secondary dark:text-dark-text-secondary">
        {(tool?.name || 'T').charAt(0).toUpperCase()}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  return (
    <>
    <div className="bg-light-bg-primary p-4 dark:bg-dark-bg-primary sm:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">Contacts</h3>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Add Contact
        </button>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Contact Form */}
      {showForm && (
        <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg mb-6 border border-light-border-primary dark:border-dark-border-primary">
          <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary mb-4">
            {editingContact ? 'Edit Contact' : 'Add New Contact'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Contact Type"
                required
                fullWidth
                placeholder="Select contact type"
                value={formData.tool_id ? String(formData.tool_id) : ''}
                onChange={(v) => setFormData(prev => ({ ...prev, tool_id: v || '' }))}
                options={tools.map((tool) => ({ value: String(tool.id), label: tool.name }))}
              />
              
              <div>
                <TextField
                  label="Value"
                  required
                  fullWidth
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Enter contact value..."
                />
              </div>
            </div>

            <Checkbox
              label="Primary contact"
              color="primary"
              size="small"
              checked={!!formData.is_primary}
              onChange={(checked) => setFormData(prev => ({ ...prev, is_primary: checked }))}
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {editingContact ? 'Update' : 'Add'} Contact
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-600 dark:bg-gray-500 hover:bg-gray-700 dark:hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Contacts List */}
      {contacts.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-light-text-tertiary dark:text-dark-text-tertiary text-lg mb-2">No contacts found</div>
          <div className="text-light-text-tertiary dark:text-dark-text-tertiary">Add a contact to get started</div>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.id} className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex items-center space-x-3">
                      {renderToolIcon(contact.tool)}
                      <h4 className="text-base font-medium text-light-text-primary dark:text-dark-text-primary">
                        {contact.tool?.name || `Tool ${contact.tool_id}`}
                      </h4>
                    </div>
                    {contact.is_primary && (
                      <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded">
                        Primary
                      </span>
                    )}
                    {contact.is_verified && (
                      <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded">
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="text-sm md:text-base text-light-text-secondary dark:text-dark-text-secondary mb-1">{contact.value}</div>
                  <div className="text-xs md:text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                    Created: {new Date(contact.created_at).toLocaleDateString('en-US')}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleEdit(contact)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm md:text-base"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(contact)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm md:text-base"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete Contact"
      message="Are you sure you want to delete this contact? This action cannot be undone."
      confirmText="Delete"
    />
    </>
  );
};

export default ContactsTab;
